/**
 * Envoltorio de campo: micro-label mono uppercase (identidad del demo), pista, error y
 * marca de obligatorio. Todo control del design system se monta adentro de esto, así el
 * `aria-describedby` y el `id` salen siempre bien atados.
 */
import { useId, type ReactNode } from "react";
import { cn } from "../../lib/cn.js";

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | undefined;
  required?: boolean;
  className?: string;
  /** Recibe los ids ya calculados: `id` para el control y `describedBy` para el texto. */
  children: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}

export function Field({ label, hint, error, required, className, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ");

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label ? (
        <label htmlFor={id} className="micro flex items-center gap-1 text-dim">
          {label}
          {required ? (
            <span aria-hidden className="text-accent-live not-italic tracking-normal">
              *
            </span>
          ) : null}
        </label>
      ) : null}

      {children({ id, describedBy: describedBy || undefined, invalid: Boolean(error) })}

      {hint && !error ? (
        <p id={hintId} className="text-[12px] leading-snug text-mute">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-[12px] leading-snug text-danger-fg">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Clases compartidas por input, textarea y select: el «control» de la identidad. */
export const controlClass =
  "w-full bg-panel border border-line rounded-[10px] px-3 text-ink placeholder:text-mute " +
  "transition-colors hover:border-line-strong focus:border-accent-live focus:outline-none " +
  "disabled:opacity-50 disabled:cursor-not-allowed";

export const controlInvalidClass = "border-danger/70 hover:border-danger focus:border-danger";
